import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PinInput } from '../components/auth/PinInput';
import { Logo } from '../components/shared/Logo';
import { authApi } from '../services/api';
import './LoginScreen.css';

export const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error: authError } = useAuth();
  const [localError, setLocalError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handlePinComplete = async (pin: string) => {
    if (!phone.trim()) {
      setLocalError('Please enter your mobile number');
      return;
    }

    setLoading(true);
    setLocalError('');

    try {
      await login(pin, phone);
      navigate('/dashboard');
    } catch (err: any) {
      setLocalError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPin = async () => {
    setForgotMsg('');
    setLocalError('');
    const trimmed = phone.trim();
    if (!trimmed) {
      setLocalError('Please enter your mobile number first, then tap Forgot PIN.');
      return;
    }
    if (!window.confirm(`Send a new PIN by SMS to ${trimmed}?`)) return;
    setForgotBusy(true);
    try {
      const msg = await authApi.forgotPin(trimmed);
      setForgotMsg(msg || 'If that mobile number is registered, a new PIN has been sent by SMS.');
    } catch (err: any) {
      setLocalError(err?.response?.data?.message || err?.message || 'Could not reset PIN. Please try again.');
    } finally {
      setForgotBusy(false);
    }
  };

  const displayError = localError || authError;

  return (
    <div className="login-screen">
      <div className="login-content">
        <div className="logo-section">
          <Logo size="large" variant="light" />
        </div>
        <div className="login-container">
          <div className="login-header">
            <h1>Welcome to Yebo Wallet</h1>
            <p>Enter your mobile number and 4-digit PIN to continue</p>
          </div>

          <div className="login-form">
            <div className="mobile-input-section">
              <label htmlFor="phone">Mobile Number</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your mobile number"
                className="mobile-input"
                disabled={loading || isLoading}
              />
            </div>

            <div className="pin-section">
              <label>PIN</label>
              <PinInput
                length={4}
                onComplete={handlePinComplete}
                error={!!displayError}
                disabled={loading || isLoading}
              /></div>

            <div className="forgot-pin-section">
              <button
                type="button"
                className="forgot-pin-link"
                onClick={handleForgotPin}
                disabled={forgotBusy || loading || isLoading}
              >
                {forgotBusy
                  ? <><i className="fas fa-spinner fa-spin"></i> Sending…</>
                  : <>Forgot PIN?</>}
              </button>
            </div>

            {displayError && (
              <div className="error-message">
                {displayError.split('\n').map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            )}

            {forgotMsg && <div className="forgot-pin-message"><i className="fas fa-check-circle"></i> {forgotMsg}</div>}

            {(loading || isLoading) && <div className="loading-spinner">Verifying...</div>}
          </div>
        </div>
      </div>
    </div>
  );
};